"use client";

import { GenderTexts } from "@entities/common";
import { Proposal } from "@entities/proposal";
import { Vacancy } from "@entities/vacancy";
import { useClientConfig } from "@providers/app-provider/client-app-provider";
import { useClientRest } from "@shared/hooks";
import { getAge, toSnake } from "@shared/utils";
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
} from "@ubud-ui/react-ui-kit";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

type FormState = {
  id: string;
  label: string;
  value: string | string[];
  checked: boolean;
  payload?: any;
  isPayloadValid?: boolean;
  errorMessage?: string;
};

type Payload = { vacancy: string; dataSharingConsent: Record<string, boolean> };

export function ExternalApplyAction({ vacancy, onSucceed }: { vacancy: Vacancy; onSucceed: (response: Proposal) => void }) {
  const clientConfig = useClientConfig();
  const user = clientConfig?.user;

  const getMaskedPhone = (phone?: string | null): string => {
    if (!phone) return "";

    const visible = phone.slice(0, -4);
    return visible + "****";
  };

  const [isFormStateChanged, setIsFormStateChanged] = useState<boolean>(false);
  const [formState, setFormState] = useState<FormState[]>([
    { id: "email", label: "Alamat Email", value: user?.email || "", checked: false },
    {
      id: "phone",
      label: "Nomor Telepon",
      value: user?.profile?.phone ? getMaskedPhone(user.profile.phone) : "",
      checked: false,
      payload: null,
      isPayloadValid: false,
      errorMessage: "4 digit terakhir dari nomor telepon harus diisi dengan tepat",
    },
    {
      id: "profile",
      label: "Profil/CV",
      value: [
        `Nama: ${user?.name}`,
        `Tempat Lahir: ${user?.profile?.pob}`,
        `Usia: ${getAge(user?.profile?.bod)} Tahun`,
        `Jenis Kelamin: ${GenderTexts[user?.profile?.gender?.toString() || "other"]}`,
        `Domisili: ${user?.profile?.domicileRegion?.name}`,
      ],
      checked: false,
    },
  ]);

  const handleUpdateFormStateChecked = (state: FormState, checked: boolean) => {
    if (state) {
      setFormState((prev) => {
        if (!prev) return prev;
        const next = [...prev];
        const index = next.findIndex((it) => it.id === state.id);

        if (index !== -1) {
          next[index].checked = checked || false;
        }

        return next;
      });
    }
  };

  const handleUpdateFormStatePayload = (state: FormState, payload: any, isValid?: boolean) => {
    if (state) {
      setFormState((prev) => {
        if (!prev) return prev;
        const next = [...prev];
        const index = next.findIndex((it) => it.id === state.id);

        if (index !== -1) {
          next[index].payload = payload || null;
          next[index].isPayloadValid = isValid || false;
        }

        return next;
      });
    }
  };

  const isFormStateValid = (state: FormState) => {
    if (state.payload === undefined) {
      return !!state.checked;
    }

    return !!state.checked && !!state.payload && !!state.isPayloadValid;
  };

  const getPayloadForm = (state: FormState) => {
    if (state && state.id === "phone") {
      const phone = user?.profile?.phone?.slice(0, -4);

      return (
        <div className="mt-2 flex items-center">
          <Input
            placeholder="Masukkan 4 digit terakhir nomor telepon Anda"
            className="relative bg-white"
            type="tel"
            inputMode="tel"
            maxLength={4}
            max={4}
            onChange={(event) =>
              handleUpdateFormStatePayload(
                state,
                event.target?.value,
                event.target?.value?.length === 4 && phone + "" + event.target.value === user?.profile?.phone,
              )
            }
          />
        </div>
      );
    }

    return <></>;
  };

  const { request } = useClientRest();
  const { mutate, isPending } = useMutation<unknown, Error, Payload>({
    mutationKey: ["external-apply-vacancy-action-" + vacancy.id],
    mutationFn: (payload) =>
      request({
        url: `/karirhub/recruitment/v2/industrial-vacancies/${vacancy.vacancyableId}/apply`,
        method: "POST",
        payload: payload,
      }),
  });
  const handleApply = () => {
    setIsFormStateChanged(true);

    const checklists: Record<string, boolean> = {
      email: false,
      phone: false,
      profile: false,
    };
    const payload = formState.map((item) => {
      const check = item.payload !== undefined ? item.checked && item.payload && item.isPayloadValid : item.checked;

      checklists[item.id] = check;

      return check;
    });

    if (payload.every((it) => !!it)) {
      mutate(
        toSnake({
          vacancy: vacancy.id,
          dataSharingConsent: checklists,
        }),
        {
          onSuccess: (response: any) => {
            if (response && response.data) {
              onSucceed(response.data);
            }
          },
        },
      );
    }
  };

  if (vacancy && vacancy.platform && !vacancy.platform.isInternal) {
    return (
      <div>
        <Button className="font-bold w-full flex text-base p-6" asChild>
          <a href={vacancy.platformLink}>Lamar Sekarang</a>
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Dialog>
        <DialogTrigger asChild>
          <Button className="font-bold w-full flex text-base p-6">Lamar Sekarang</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader className="text-left">
            <DialogTitle className="text-xl">Konfirmasi Melamar Lowongan: {vacancy.title}</DialogTitle>
            <DialogDescription>
              Sebelum melamar lowongan ini, pastikan Anda menyetujui data apa saja yang akan dibagikan ke perusahaan penyedia lowongan.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div>
              <Alert variant="default" className="bg-primary-50 border-primary-200 text-primary-700">
                <AlertDescription>
                  Dengan melanjutkan proses lamaran, CV/Profil dan informasi kontak Anda akan dibagikan kepada HR perusahaan terkait serta
                  HiredToday sebagai bagian dari proses rekrutmen pada lowongan tersebut.
                </AlertDescription>
              </Alert>
              <div className="space-y-4 mt-4">
                {formState.map((state, index) => {
                  return (
                    <div key={state.id + "-" + index}>
                      <div
                        className={cn(
                          "flex items-start gap-3 relative cursor-pointer border rounded-lg p-4",
                          !isFormStateValid(state) && isFormStateChanged ? "bg-danger-50 border-danger-500" : "bg-white border-gray-200",
                        )}
                      >
                        <label className="cursor-pointer absolute top-0 left-0 w-full h-full" htmlFor={state.id}></label>
                        <Checkbox
                          className="mt-1"
                          id={state.id}
                          onCheckedChange={(checked) => handleUpdateFormStateChecked(state, checked as boolean)}
                          checked={state.checked}
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-base">{state.label}</h4>
                          <div className="text-sm text-gray-500">{Array.isArray(state.value) ? state.value.join(", ") : state.value}</div>
                          {state.payload !== undefined && state.checked && getPayloadForm(state)}
                        </div>
                      </div>
                      {!isFormStateValid(state) && isFormStateChanged && state.errorMessage && (
                        <div className="bg-danger-100 text-center text-danger-600 text-sm border border-danger-500 rounded-xl px-2 pb-2 pt-5 -mt-4">
                          {state.errorMessage}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </DialogBody>
          <DialogFooter className="mt-4">
            <div className="flex items-center justify-end">
              <Button className="font-bold text-base p-6" onClick={handleApply} disabled={!!isPending}>
                {isPending ? "Sedang melamar..." : "Lamar Lowongan"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
